"use client";

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";

export type Category = {
    id: string;
    name: string;
    color?: string | null;
};

interface CategoriesSectionProps {
    onCategorySelect?: (category: Category) => void;
}

export function CategoriesSection({ onCategorySelect }: CategoriesSectionProps) {
    const [activeCategory, setActiveCategory] = useState("all");

    const { data, isLoading } = useQuery({
        queryKey: queryKeys.events.categories(),
        queryFn: () => apiFetch<Category[]>("categories"),
        staleTime: 30 * 60_000,
    });

    const categories = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }
        return [{ id: "all", name: "All", color: null }, ...data.slice(0, 10)];
    }, [data]);

    const handleSelect = (category: Category) => {
        setActiveCategory(category.id);
        onCategorySelect?.(category);
    };

    if (isLoading) {
        return (
            <section className="px-4 py-1 md:px-6 md:py-4" aria-busy="true" aria-label="Loading categories">
                <div className="mx-auto max-w-6xl">
                    <div className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:pb-2">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div
                                key={i}
                                className="h-9 w-20 flex-shrink-0 animate-pulse rounded-full bg-[var(--hopp-panel)]"
                            />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (categories.length === 0) {
        return null;
    }

    return (
        <section className="px-4 py-1 md:px-6 md:py-4" aria-label="Event categories">
            <div className="mx-auto max-w-6xl">
                {/* Horizontal Scroll Container */}
                <div className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:pb-2" role="tablist" aria-label="Filter by category">
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            role="tab"
                            aria-selected={activeCategory === category.id}
                            onClick={() => handleSelect(category)}
                            className={`flex-shrink-0 rounded-full px-4 py-2.5 text-xs font-medium transition-all duration-150 ${activeCategory === category.id
                                    ? "border border-white/30 bg-white/15 text-[#EDEDED] shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]"
                                    : "border border-white/10 bg-white/5 text-[#EDEDED]/60 hover:border-white/20 hover:text-[#EDEDED] hover:bg-white/10"
                                }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
